import { useState } from "react";
import { Check } from "lucide-react";
import { toast } from "sonner";
import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function PartnerPage() {
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [form, setForm] = useState({
    organization_name: "", contact_name: "", email: "", message: ""
  });
  const [errors, setErrors] = useState({});

  const validate = () => {
    const e = {};
    if (!form.organization_name.trim()) e.organization_name = "Required";
    if (!form.contact_name.trim()) e.contact_name = "Required";
    if (!form.email.trim()) e.email = "Required";
    else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email)) e.email = "Invalid email";
    if (!form.message.trim() || form.message.length < 20) e.message = "Minimum 20 characters";
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const submit = async (ev) => {
    ev.preventDefault();
    if (!validate()) return;
    setSubmitting(true);
    try {
      await axios.post(`${API}/partner-contact`, form);
      setSubmitted(true);
      toast.success("Inquiry submitted");
    } catch {
      toast.error("Submission failed");
    } finally {
      setSubmitting(false);
    }
  };

  const update = (field, value) => {
    setForm(f => ({ ...f, [field]: value }));
    if (errors[field]) setErrors(e => ({ ...e, [field]: undefined }));
  };

  if (submitted) {
    return (
      <div className="min-h-[80vh] flex items-center justify-center bg-paper" data-testid="partner-success">
        <div className="max-w-md text-center px-6">
          <div className="h-16 w-16 border border-black/10 flex items-center justify-center mx-auto mb-6">
            <Check className="h-8 w-8 text-crimson" strokeWidth={1.5} />
          </div>
          <h1 className="font-serif text-3xl text-ink mb-4">Inquiry Received</h1>
          <p className="text-muted mb-8">
            Thank you for your interest. Our team will review and respond shortly.
          </p>
          <button 
            onClick={() => window.location.href = "/"}
            className="bg-ink text-white px-8 py-3 text-xs tracking-[0.2em] uppercase"
            data-testid="return-home-btn"
          >
            Return Home
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-[80vh] bg-paper py-16" data-testid="partner-page">
      <div className="max-w-2xl mx-auto px-6">
        
        {/* Header */}
        <div className="mb-12">
          <p className="text-[10px] tracking-[0.3em] uppercase text-muted mb-3">Partnership</p>
          <h1 className="font-serif text-4xl text-ink">Partner With Us</h1>
          <p className="text-muted mt-4 max-w-lg">
            Join our network of international organizations committed to youth development and global leadership.
          </p>
        </div>

        {/* Form */}
        <form onSubmit={submit} className="bg-white border border-black/10 p-8 lg:p-12">
          <div className="space-y-6">
            <div>
              <label className="block text-[10px] tracking-[0.2em] uppercase text-muted mb-2">Organization Name *</label>
              <input
                value={form.organization_name}
                onChange={e => update("organization_name", e.target.value)}
                className={`w-full border p-4 bg-transparent focus:outline-none focus:border-ink ${errors.organization_name ? 'border-crimson' : 'border-black/15'}`}
                placeholder="Your organization"
                data-testid="input-organization"
              />
              {errors.organization_name && <p className="text-crimson text-xs mt-1">{errors.organization_name}</p>}
            </div>

            <div>
              <label className="block text-[10px] tracking-[0.2em] uppercase text-muted mb-2">Contact Person *</label>
              <input
                value={form.contact_name}
                onChange={e => update("contact_name", e.target.value)}
                className={`w-full border p-4 bg-transparent focus:outline-none focus:border-ink ${errors.contact_name ? 'border-crimson' : 'border-black/15'}`}
                placeholder="Your name"
                data-testid="input-contact-name"
              />
              {errors.contact_name && <p className="text-crimson text-xs mt-1">{errors.contact_name}</p>}
            </div>

            <div>
              <label className="block text-[10px] tracking-[0.2em] uppercase text-muted mb-2">Email *</label>
              <input
                type="email"
                value={form.email}
                onChange={e => update("email", e.target.value)}
                className={`w-full border p-4 bg-transparent focus:outline-none focus:border-ink ${errors.email ? 'border-crimson' : 'border-black/15'}`}
                placeholder="your@organization.com"
                data-testid="input-email"
              />
              {errors.email && <p className="text-crimson text-xs mt-1">{errors.email}</p>}
            </div>

            <div>
              <label className="block text-[10px] tracking-[0.2em] uppercase text-muted mb-2">Message *</label>
              <textarea
                value={form.message}
                onChange={e => update("message", e.target.value)}
                rows={5}
                className={`w-full border p-4 bg-transparent focus:outline-none focus:border-ink resize-none ${errors.message ? 'border-crimson' : 'border-black/15'}`}
                placeholder="Describe how you'd like to collaborate..."
                data-testid="input-message"
              />
              {errors.message && <p className="text-crimson text-xs mt-1">{errors.message}</p>}
            </div>
          </div>

          <div className="mt-8 pt-6 border-t border-black/10">
            <button
              type="submit"
              disabled={submitting}
              className="w-full bg-crimson hover:bg-[#6B130B] text-white py-4 text-xs tracking-[0.2em] uppercase disabled:opacity-50"
              data-testid="submit-partner-btn"
            >
              {submitting ? "Sending..." : "Send Inquiry"}
            </button>
          </div>
        </form>

        <p className="text-center text-sm text-muted mt-8">
          Direct inquiries: partnerships@globalyouthplatform.org
        </p>

      </div>
    </div>
  );
}
